"use client";

import { useState } from "react";

import { useClose } from "@headlessui/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { acceptInvite, deleteInvite } from "@/api";
import {
  NotificationAcceptButton,
  NotificationActions,
  NotificationDeclineButton,
} from "@/components/doc/left-sidebar/top-menu/notifications/NotificationActions";
import { NotificationRow } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationRow";
import { useNotificationsPanelOptions } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationsPanelOptions";
import { useStore } from "@/store";
import { teamPageUrl } from "@/urls";
import { trackEvent } from "@/utils/trackEvent";

import type { Invite, Notification } from "@/api";

export function InviteNotificationItem({
  notification,
  entity,
}: {
  notification: Notification;
  entity: Invite;
}) {
  const t = useTranslations("sidebar.notifications");
  const { teams } = useStore();
  const router = useRouter();
  const close = useClose();
  const { disableNavigation } = useNotificationsPanelOptions();
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [resolved, setResolved] = useState(false);

  // TODO: handle errors https://linear.app/corca-research/issue/D-2963
  const onAccept = async () => {
    if (busy) return;
    trackEvent("notification_action_clicked", { type: "invite", action: "accept" });
    setBusy("accept");
    try {
      await acceptInvite(entity.token);
      setResolved(true);
      if (entity.team && !disableNavigation) {
        // Refresh memberships first so the team page recognizes the new team;
        // redirect even if the refresh fails, the team page still resolves
        await teams.fetchTeams().catch(() => {});
        close();
        router.push(teamPageUrl({ teamPermalink: entity.team.permalink }));
      }
    } finally {
      setBusy(null);
    }
  };

  const onDecline = async () => {
    if (busy) return;
    trackEvent("notification_action_clicked", { type: "invite", action: "decline" });
    setBusy("decline");
    try {
      await deleteInvite(entity.token);
      setResolved(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <NotificationRow notification={notification} eventType="invite" badge="invite">
      <p className="text-[15px] leading-[18px] tracking-[-0.02em]">
        {t.rich("invite.body", {
          senderName: notification.sender.name,
          name: entity.team?.title ?? "",
          user: (chunks) => (
            <Link
              href={teamPageUrl({ teamPermalink: notification.sender.username })}
              className="font-semibold hover:underline"
            >
              {chunks}
            </Link>
          ),
          team: (chunks) =>
            entity.team ? (
              <Link
                href={teamPageUrl({ teamPermalink: entity.team.permalink })}
                className="font-semibold hover:underline"
              >
                {chunks}
              </Link>
            ) : (
              <strong className="font-semibold">{chunks}</strong>
            ),
        })}
      </p>
      {!resolved && (
        <NotificationActions>
          <NotificationDeclineButton disabled={!!busy} onClick={onDecline}>
            {t("invite.decline")}
          </NotificationDeclineButton>
          <NotificationAcceptButton isBusy={busy === "accept"} onClick={onAccept}>
            {t("invite.accept")}
          </NotificationAcceptButton>
        </NotificationActions>
      )}
    </NotificationRow>
  );
}
